import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  CheckEntityDomainExistedOption,
  EntityApi,
  EntityInfo,
  EntityInitInfo,
  FindBillCustomersBody,
  FindBillCustomersOption,
  FindDefaultGroupAndPolicyForCreatePartnerOption,
  FindEntitiesForSyncProductsType,
  FindEntityToConnectOption,
  FindOneOptions,
  FindOptions,
  FindWithAuthEntityInChainOption,
  FindWithAuthOption,
  SearchEntityToConnectOption,
  SuggestEntityToConnectOption,
  WardApi,
} from 'api';
import { updateCurrentEntity } from 'api/services';
import { updateCurrentEntityBlog } from 'api/services/blog';
import { updateCurrentEntityStock } from 'api/services/stock';
import { SELECTED_ENTITY } from 'types/localStorage';
import { ENTITY_TYPE } from 'utils';

import { RootState } from '../app';
import { logout, refreshAccessToken } from './authSlice';
import {
  createEntityDiary,
  removeEntityDiary,
  updateEntityDiary,
} from './entityDiarySlice';
import {
  createOption,
  deleteOption,
  findOptionsWithAuth,
  updateOption,
} from './optionSlice';

export const findChainEntities = createAsyncThunk(
  'account/findChainEntities',
  async (
    {
      centralEntityID,
      option,
    }: { centralEntityID: string; option: FindOptions },
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityApi.findChainEntities(
        centralEntityID,
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findChainSuppliers = createAsyncThunk(
  'account/findChainSuppliers',
  async ({ supplierName }: { supplierName: string }, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findChainSuppliers({ supplierName });

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findUserPermissions = createAsyncThunk(
  'account/findUserPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findUserPermissions();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findWithAuth = createAsyncThunk(
  'account/findWithAuth',
  async (option: FindWithAuthOption, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findWithAuth(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findMembers = createAsyncThunk(
  'account/findMembers',
  async (params: { body: any; option?: any }, { rejectWithValue }) => {
    try {
      const { body, option } = params;
      const response = await EntityApi.findMembers(option, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findWithAuthEntityInChain = createAsyncThunk(
  'account/findWithAuthEntityInChain',
  async (
    params: { entityID: string; option?: FindWithAuthEntityInChainOption },
    { rejectWithValue }
  ) => {
    try {
      const { entityID, option } = params;
      const response = await EntityApi.findWithAuthEntityInChain(
        entityID,
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findWithAuthHavePagination = createAsyncThunk(
  'account/findWithAuthHavePagination',
  async (option: FindWithAuthOption, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findWithAuth(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOverviewCertificationsInfo = createAsyncThunk(
  'account/findOverviewCertificationsInfo',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findOverviewCertificationsInfo(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findContactInfo = createAsyncThunk(
  'account/findContactInfo',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findContactInfo(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findSeoInfo = createAsyncThunk(
  'account/findSeoInfo',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findSeoInfo(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findForWebsiteInfo = createAsyncThunk(
  'account/findForWebsiteInfo',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findForWebsiteInfo(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findCatalogForWebsiteInfo = createAsyncThunk(
  'account/findCatalogForWebsiteInfo',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findCatalogForWebsiteInfo(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOverviewInfo = createAsyncThunk(
  'account/findOverviewInfo',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findOverviewInfo(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const selectOneAccount = createAsyncThunk(
  'account/selectOneAccount',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findOneForSelect(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntitiesForSyncProducts = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>(
  'account/findEntitiesForSyncProducts',
  async (option: FindEntitiesForSyncProductsType, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findEntitiesForSyncProducts(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findCustomerEntitiesForSyncProducts = createAsyncThunk<
  any,
  any,
  { rejectValue: { message: string } }
>(
  'account/findCustomerEntitiesForSyncProducts',
  async (option: FindEntitiesForSyncProductsType, { rejectWithValue }) => {
    try {
      const response =
        await EntityApi.findCustomerEntitiesForSyncProductsOptimize(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityToConnect = createAsyncThunk(
  'account/findEntityToConnect',
  async (option: FindEntityToConnectOption, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findEntityToConnect(option);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findBillCollaborators = createAsyncThunk(
  'account/findBillCollaborators',
  async (_, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findBillCollaborators();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);
export const findBillCustomers = createAsyncThunk(
  'account/findBillCustomers',
  async (
    {
      query,
      body,
    }: { query: FindBillCustomersOption; body: FindBillCustomersBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await EntityApi.findBillCustomers(query, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneUser = createAsyncThunk(
  'account/findOneUser',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findOneUser(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneWard = createAsyncThunk(
  'account/findOneWard',
  async (
    { id, options }: { id: string; options: FindOneOptions },
    { rejectWithValue }
  ) => {
    try {
      const response = await WardApi.findOne(id, options);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateAccount = createAsyncThunk(
  'account/updateAccount',
  async (params: Param, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await EntityApi.update(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateForecastShareKey = createAsyncThunk(
  'account/updateForecastShareKey',
  async (params: Param, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await EntityApi.updateShareKey(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateCheckForecastShareKey = createAsyncThunk(
  'account/updateCheckForecastShareKey',
  async (params: Param, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await EntityApi.updateCheckShareKey(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEntityTheme = createAsyncThunk(
  'account/updateEntityTheme',
  async (params: Param, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await EntityApi.updateTheme(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEntityThemeFrames = createAsyncThunk(
  'account/updateEntityThemeFrames',
  async (params: Param, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await EntityApi.updateThemeFrames(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEntitySEO = createAsyncThunk(
  'account/updateEntitySEO',
  async (params: Param, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await EntityApi.updateSEO(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAccount = createAsyncThunk(
  'account/createAccount',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await EntityApi.create(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createCustomDomain = createAsyncThunk(
  'account/createCustomDomain',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await EntityApi.createCustomDomain(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAnonymous = createAsyncThunk(
  'account/createAnonymous',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await EntityApi.createAnonymous(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkDuplicateCouponCode = createAsyncThunk(
  'area/checkDuplicateCouponCode',
  async (query: any, { rejectWithValue }) => {
    try {
      const response = await EntityApi.checkDuplicateCouponCode(query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeEntityEntityProposalPolicy = createAsyncThunk(
  'account/removeEntityEntityProposalPolicy',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityApi.removeEntityProposalPolicy(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findDefaultGroupAndPolicyForCreatePartner = createAsyncThunk(
  'account/findDefaultGroupAndPolicyForCreatePartner',
  async (
    options: FindDefaultGroupAndPolicyForCreatePartnerOption,
    { rejectWithValue }
  ) => {
    try {
      const response =
        await EntityApi.findDefaultGroupAndPolicyForCreatePartner(options);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityInfoForConfirmConnect = createAsyncThunk(
  'account/findEntityInfoForConfirmConnect',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findEntityInfoForConfirmConnect(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const suggestEntityToConnect = createAsyncThunk(
  'account/suggestEntityToConnect',
  async (option: SuggestEntityToConnectOption, { rejectWithValue }) => {
    try {
      const response = await EntityApi.suggestEntityToConnect(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const searchEntityToConnect = createAsyncThunk(
  'account/searchEntityToConnect',
  async (option: SearchEntityToConnectOption, { rejectWithValue }) => {
    try {
      const response = await EntityApi.searchEntityToConnect(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkDuplicateDomain = createAsyncThunk(
  'account/checkDuplicateDomain',
  async (option: CheckEntityDomainExistedOption, { rejectWithValue }) => {
    try {
      const response = await EntityApi.checkDuplicateDomain(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface IInitialState {
  list: Array<any>;
  option: {
    list: any[];
    total: number;
  };
  listWithRole: Array<any>; // Using for role employee or manager
  item: EntityInitInfo | any | undefined;
  owner: any;
  isToggle: boolean;
  isHaveChain: boolean;
  reloadFlag: boolean;
  isSelectedAccount: boolean;
  isFirstSelected: boolean;
  reloadOptionsFlag: boolean;
  isFirstLoading: boolean;
  totalAccount?: number;
  isLoadListWithRole: boolean;
  isFirstSelectedAccount: boolean;
  isReloadThenDeleteOption: boolean;
  isUpdateOption: boolean;
  isInvalidAccessDomain: boolean;
  isDomainNotFound: boolean;
  actionRole?: number;
  permissions: any;
  roleTitle: any;
  isInitLoad: boolean;
  uploadAvatarFile?: any;
}

const initialState: IInitialState = {
  list: [],
  listWithRole: [],
  option: {
    list: [],
    total: 0,
  },
  item: undefined,
  owner: undefined,
  isToggle: true,
  isHaveChain: false,
  reloadFlag: false,
  isSelectedAccount: true,
  isFirstSelected: false,
  reloadOptionsFlag: false,
  isFirstLoading: true,
  totalAccount: 0,
  isLoadListWithRole: false,
  isFirstSelectedAccount: true,
  isReloadThenDeleteOption: false,
  isUpdateOption: false,
  actionRole: undefined,
  isInvalidAccessDomain: false,
  isDomainNotFound: false,
  permissions: [],
  roleTitle: '',
  isInitLoad: true,
  uploadAvatarFile: undefined,
};

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    updateInitLoad(state) {
      state.isInitLoad = false;
    },
    updateOwnerInfo(state, action) {
      if (state?.item) {
        state.owner = {
          ...state.owner,
          ...action.payload,
        };
      }
    },
    clearData(state) {
      Object.entries(initialState).forEach(item => {
        const [key, value] = item;
        state[key] = value;
      });
      state.isInitLoad = true;
    },
    clearSelectedEntity(state) {
      state.item = undefined;
    },
    reloadAccounts(state) {
      state.reloadFlag = !state.reloadFlag;
    },
    updateUploadAvatarFile(state, action) {
      state.uploadAvatarFile = action.payload;
    },
    updateAccountInfo(state, action) {
      if (state?.item) {
        state.item = {
          ...state.item,
          ...action.payload,
        };
      }
    },
    uploadEntityList(state, action) {
      state.list = action.payload;
    },
    updateQuantity(state, action) {
      const { count, type } = action.payload;
      if (state.item && state.item[type] != undefined) {
        state.item[type] += count;
      }
    },
    updateIsReloadThenDeleteOption(state, action) {
      state.isReloadThenDeleteOption = action.payload;
    },
    updateIsUpdateOption(state, action) {
      state.isUpdateOption = action.payload;
    },

    updateMultiTypeQuantity(state, action) {
      const dataUpdate = action.payload;
      dataUpdate.forEach(item => {
        if (state.item && state.item[item.type] != undefined) {
          state.item[item.type] += item.count;
        }
      });
    },

    updateUserQuantity(state, action) {
      const { count, type } = action.payload;
      if (state.owner && state.owner[type] != undefined) {
        state.owner[type] += count;
      }
    },

    updateMenuToggle(state, action) {
      const toggle = action.payload;
      state.isToggle = toggle;
    },

    updateOptionOfEntity(state, action) {
      const { oldOptionID, newOption, entityID } = action.payload;
      const index = state.option.list.findIndex(
        item => item?.id == newOption?.id
      );

      if (index >= 0) {
        state.option.list[index].count++;
      } else {
        state.option.list.push({
          colorCode: newOption.color.code,
          content: newOption.content,
          count: 1,
          name: newOption.color.name,
          type: newOption.type,
          id: newOption.id,
        });
      }

      state.option.total++;

      if (oldOptionID) {
        const indexOldOption = state.option.list.findIndex(
          item => item.id == oldOptionID
        );

        if (typeof indexOldOption && indexOldOption >= 0) {
          state.option.list[indexOldOption].count--;
          state.option.total--;
        }
      }

      if (state.item && newOption && entityID == state?.item?.id) {
        if (state.item.entityOptions?.length == 0) {
          state.item.entityOptions.push({
            option: {
              id: newOption.id,
              content: newOption.content,
              color: {
                code: newOption.color.code,
              },
            },
          });
        } else {
          if (!state?.item?.entityOptions) {
            state.item.entityOptions = [
              {
                option: {
                  id: newOption.id,
                  content: newOption.content,
                  color: {
                    code: newOption.color.code,
                  },
                },
              },
            ];
          } else {
            state.item.entityOptions[0].option.color.code =
              newOption.color.code;
            state.item.entityOptions[0].option.content = newOption.content;
            state.item.entityOptions[0].option.id = newOption.id;
          }
        }
      }

      state.isUpdateOption = true;
      state.option.list = state.option.list.sort((a, b) => b?.count - a?.count);
      state.option = { ...state.option };
    },
    updateIsCentralEntity(
      state,
      action: { payload: { id: string; isCentral: boolean } }
    ) {
      const { id, isCentral } = action.payload;
      const updatedEntity = state.list.find(item => item.id === id);
      updatedEntity.isCentral = isCentral;
    },
    updateAccountItem(state, action: { payload: EntityInfo }) {
      const { id } = action.payload;
      if (state.item?.id == id) {
        Object.keys(action.payload).forEach(key => {
          if (state.item && state.item[key] != undefined) {
            state.item[key] = action.payload[key];
          }
        });
      }
    },
    updateDomainNotFound(state, action: { payload }) {
      state.isDomainNotFound = action.payload;
      state.isInvalidAccessDomain = !action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(findWithAuth.fulfilled, (state, action) => {
      const result: any = action.payload;
      const { data } = result;
      const { centralEntities } = result;

      let isHaveChain = false;

      if (centralEntities?.length) {
        isHaveChain = true;
      }

      const centralNewData = centralEntities.map((item: any) => {
        return {
          ...item,
          key: item.id,
          img: item.avatar,
        };
      });

      const newData = data.map((item: any) => {
        return {
          ...item,
          key: item.id,
          img: item.avatar,
        };
      });

      state.isHaveChain = isHaveChain;
      state.list = [...newData, ...centralNewData];
      if (state.item) {
        const currentEntityRole = state.list.find(
          item => item.id === state.item?.id
        )?.role;
        state.item.role = currentEntityRole;
      }
    });
    builder.addCase(refreshAccessToken.rejected, (state, action) => {
      clearData();
    });
    builder.addCase(findOptionsWithAuth.fulfilled, (state, action) => {
      const { data, pagination } = action.payload as any;
      state.option = {
        list: data,
        total: pagination.total,
      };
      state.reloadOptionsFlag = false;
    });

    builder.addCase(findWithAuthEntityInChain.fulfilled, (state, action) => {
      const { data } = action.payload;

      const newData = data.map((item: any) => {
        return {
          ...item,
          key: item.id,
          img: item.avatar,
        };
      });
      state.listWithRole = newData;
      state.isFirstSelected = false;
      state.isLoadListWithRole = false;
    });

    builder.addCase(updateAccount.fulfilled, (state, action) => {
      // TODO: check after update
      const { data } = action.payload;
      state.item = {
        ...state.item,
        ...data,
      };
    });

    builder.addCase(logout.fulfilled, (state, action) => {
      state.option = {
        list: [],
        total: 0,
      };

      state.reloadOptionsFlag = true;
      state.isFirstLoading = true;
      state.totalAccount = 0;
      state.isFirstSelectedAccount = true;
      state.isInvalidAccessDomain = false;
      state.isDomainNotFound = false;
    });

    builder.addCase(deleteOption.fulfilled, (state, action) => {
      const { data }: any = action.payload;
      const index = state.option.list.findIndex(item => item.id == data?.id);
      if (typeof index == 'number' && index >= 0) {
        state.option.total -= state.option.list[index].count;
        state.option.list = state.option.list.filter(
          item => item.id != data.id
        );
      }

      if (
        state.item &&
        state.item.entityOptions.length > 0 &&
        state.item.entityOptions[0]?.option?.id == data?.id
      ) {
        state.item.entityOptions = [];
      }

      state.option = { ...state.option };
      state.isReloadThenDeleteOption = true;
    });

    builder.addCase(findWithAuthHavePagination.fulfilled, (state, action) => {
      const { data, pagination, centralEntities }: any = action.payload;

      const isHaveChain = centralEntities?.length;

      const centralNewData = centralEntities.map((item: any) => {
        return {
          ...item,
          key: item.id,
          img: item.avatar,
        };
      });

      const newData = data.map((item: any) => {
        return {
          ...item,
          key: item.id,
          img: item.avatar,
        };
      });

      if (state.totalAccount == 0 && pagination?.total) {
        state.totalAccount = pagination?.total;
      }

      state.isFirstLoading = false;
      state.isHaveChain = isHaveChain;
      state.list = [...newData, ...centralNewData];

      state.isInvalidAccessDomain = false;
      state.isDomainNotFound = false;
    });

    builder.addCase(
      findWithAuthHavePagination.rejected,
      (state, action: any) => {
        if (action.payload?.message === 'Invalid access first time domain') {
          state.isInvalidAccessDomain = true;
        }
        if (action.payload?.message === 'Domain not found') {
          state.isDomainNotFound = true;
        }
      }
    );

    builder.addCase(createOption.fulfilled, (state, action) => {
      const { data }: any = action.payload;

      state.option.list.push({ ...data, count: 0 });
      state.option = { ...state.option };
    });

    builder.addCase(selectOneAccount.pending, (state, action) => {
      state.isSelectedAccount = true;
      state.isFirstSelected = true;
    });

    builder.addCase(selectOneAccount.rejected, (state, action) => {
      state.isFirstSelected = false;
      state.isSelectedAccount = false;
      localStorage.removeItem(SELECTED_ENTITY);
    });

    builder.addCase(selectOneAccount.fulfilled, (state, action) => {
      const { data } = action.payload;
      const { id, type } = data;
      const canUpdateDelete = data.isCentral ? true : !data.centralEntityID;
      const canCreateNewProduct =
        data.isCentral ||
        (!data.centralEntityID && data.type !== ENTITY_TYPE.COLLABORATOR);

      state.item = {
        ...data,
        canUpdateDelete,
        canCreateNewProduct,
      };
      localStorage.setItem(
        SELECTED_ENTITY,
        JSON.stringify({ type, id, role: data.role })
      );
      updateCurrentEntity(id, type);
      updateCurrentEntityBlog(id, type);
      updateCurrentEntityStock(id, type);

      state.isFirstLoading = false;
      state.isLoadListWithRole = true;
      state.isFirstSelectedAccount = false;
    });

    builder.addCase(findOneUser.fulfilled, (state, action) => {
      const { data } = action.payload;
      state.owner = data;
    });

    builder.addCase(findUserPermissions.fulfilled, (state, action) => {
      const { data } = action.payload;
      state.permissions = data.permission;
      state.roleTitle = data.title;
      state.isSelectedAccount = false;
    });

    builder.addCase(findUserPermissions.rejected, (state, action) => {
      state.isSelectedAccount = false;
    });

    builder.addCase(updateOption.fulfilled, (state, action) => {
      const { data } = action.payload;

      state.option.list = state.option.list.map(item => {
        if (item.id == data.id) {
          return {
            ...item,
            content: data.content,
          };
        }

        return item;
      });
    });
    builder.addCase(createAccount.fulfilled, (state, action) => {
      const data = action.payload;
      // const { type, id } = data;
      const newItem = {
        ...data,
        key: data.id,
        img: data.avatar,
        ...accountItemSkeleton,
      };
      state.list = state.list.concat(newItem);
      // state.item = newItem;

      // localStorage.setItem(SELECTED_ENTITY, JSON.stringify({ type, id }));
      // updateCurrentEntity(id, type);
      state.reloadOptionsFlag = true;
    });
    builder.addCase(createEntityDiary.fulfilled, (state, action) => {
      state.owner.isDiaryVerified = true;
    });
    builder.addCase(updateEntityDiary.fulfilled, (state, action) => {
      state.owner.isDiaryVerified = true;
    });
    builder.addCase(removeEntityDiary.fulfilled, (state, action) => {
      state.owner.isDiaryVerified = true;
    });
  },
});

const { actions, reducer: accountReducer } = accountSlice;

export const {
  clearData,
  clearSelectedEntity,
  reloadAccounts,
  updateInitLoad,
  updateOwnerInfo,
  updateAccountInfo,
  updateQuantity,
  updateUserQuantity,
  updateMenuToggle,
  updateOptionOfEntity,
  updateIsReloadThenDeleteOption,
  updateIsUpdateOption,
  updateUploadAvatarFile,
  updateAccountItem,
  updateDomainNotFound,
} = actions;
export default accountReducer;

export interface Param {
  id: string;
  option?: FindOneOptions;
  body?: Record<string, any>;
}

const accountItemSkeleton = {
  totalFarmAccount: 0,
  totalStoreAccount: 0,
  totalCollaboratorAccount: 0,
  totalWarehouseAccount: 0,
  totalTransportAccount: 0,
  totalVolunteerAccount: 0,
  totalPrice: 0,
  totalReceivedPrice: 0,
  totalUsedPrice: 0,
  totalProduct: 0,
  totalCoBuy: 0,
  totalDigitalMenu: 0,
  totalCombo: 0,
  totalCoupon: 0,
  totalCampaign: 0,
  totalEmployee: 0,
  totalPartner: 0,
  totalFieldEntityProductObject: 0,
  totalFarmingEntityProductObject: 0,
  totalProductionEntityProductObject: 0,
  totalWikiImage: 0,
  totalWikiFile: 0,
  totalEntityProcess: 0,
  totalEntityArea: 0,
  totalBranch: 0,
  totalFollower: 0,
  totalRating: 0,
  avgRating: 0,
};

export const selectAccount = (state: RootState): IInitialState =>
  state.account as IInitialState;
